import axios, { AxiosResponse } from 'axios';
import { createDataObject } from '../lib/utils';
import { isSettlementTransferLookupFailure, SettlementTransferLookupError } from '../features/settlementClassification/transferLookup';
import type {
    MemberNotificationSettingsApiResult,
    MemberNotificationSettingsPatchBody,
} from '../lib/memberNotificationSettings';

export /**
 * @method get
 * @brief 관리자웹의 강사 리스트 드롭다운을 위한 강사이름, coachID를 불러오는 API
 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1136/src/docs/Center.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9B%B9%EC%9D%98-%EA%B0%95%EC%82%AC-%EB%A6%AC%EC%8A%A4%ED%8A%B8-%EB%93%9C%EB%A1%AD%EB%8B%A4%EC%9A%B4%EC%9D%84-%EC%9C%84%ED%95%9C-%EA%B0%95%EC%82%AC%EC%9D%B4%EB%A6%84-coachid%EB%A5%BC-%EB%B6%88%EB%9F%AC%EC%98%A4%EB%8A%94-api
 */
    const getCoachNameListAPI = async (centerId: string, accessToken: string, includeDeleted?: boolean): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'get',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/coach-name-list/${centerId}/`,
                headers: { authorization: accessToken },
                params: includeDeleted ? { includeDeleted: 'true' } : undefined,
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            switch (error.response.status) {
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };
                case 406:
                    return { status: error.response.status };
                case 500:
                    alert('서버 내부 오류');
                    return { status: error.response.status };
            }
        }
    };

export /**
 * @method get
 * @brief 관리자의 센터 전체 활성회원 조회
 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1136/src/docs/Center.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%EC%84%BC%ED%84%B0-%EC%A0%84%EC%B2%B4-%ED%99%9C%EC%84%B1%ED%9A%8C%EC%9B%90-%EC%A1%B0%ED%9A%8C
 */
    const getCenterMemberListAPI = async (
        centerId: string,
        status: GetCenterMemberListStatus,
        remainingPeriod: GetCenterMemberListRemainingPeriod,
        remainingSessions: GetCenterMemberListRemainingSessions,
        assignedCoach: 'all' | string,
        accessToken: string
    ): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'get',
                url: `${process.env.REACT_APP_SERVER}/v2/centers/member-list/${centerId}?status=${status}&remainingPeriod=${remainingPeriod}&remainingSessions=${remainingSessions}&assignedCoach=${assignedCoach}`,
                headers: { authorization: accessToken },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            switch (error.response.status) {
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };
                case 406:
                    return { status: error.response.status };
                case 500:
                    alert('서버 내부 오류');
                    return { status: error.response.status };
            }
        }
    };
export /**
 * @method patch
 * @brief 관리자의 회원 정보 수정
 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1136/src/docs/Center.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%ED%9A%8C%EC%9B%90-%EC%A0%95%EB%B3%B4-%EC%88%98%EC%A0%95
 */
    const patchCenterUpdateMember = async ({
        centerId,
        memberId,
        name,
        gender,
        birthday,
        phoneNumber,
        memo,
        newCoachId,
        address,
        goal,
        accessToken,
    }: PatchCenterUpdateMemberParamsType): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        const data = await createDataObject({
            name,
            gender,
            birthday,
            phoneNumber,
            memo,
            newCoachId,
            goal,
            address,
        });

        try {
            response = await axios({
                method: 'patch',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/update-member/${centerId}/${memberId}`,
                headers: { authorization: accessToken },
                data: data,
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            switch (error.response.status) {
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };

                case 500:
                    alert('서버 내부 오류');
                    return { status: error.response.status };
            }
        }
    };

export /**
 * @method get
 * @brief 관리자의 회원 스케줄 기록 조회
 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1136/src/docs/Center.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%ED%9A%8C%EC%9B%90-%EC%8A%A4%EC%BC%80%EC%A4%84-%EA%B8%B0%EB%A1%9D-%EC%A1%B0%ED%9A%8C
 */
    const getMemberScheduleHistory = async (
        centerId?: string,
        coachId?: string,
        memberId?: string,
        date?: string,
        accessToken?: string
    ): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'get',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/member-schedule-history/${centerId}/${coachId}/${memberId}?date=${date}`,
                headers: { authorization: accessToken },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            switch (error.response.status) {
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };
                case 406:
                    return { status: error.response.status };
                case 500:
                    alert('서버 내부 오류');
                    return { status: error.response.status };
            }
        }
    };
export /**
 * @method patch
 * @brief 관리자의 선택 회원 다중 연결해제 API
 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1136/src/docs/Center.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%EC%84%A0%ED%83%9D-%ED%9A%8C%EC%9B%90-%EB%8B%A4%EC%A4%91-%EC%97%B0%EA%B2%B0%ED%95%B4%EC%A0%9C-api
 */
    const patchDisconnectMembers = async ({
        centerId,
        memberIds,
        accessToken,
    }: PatchDisconnectMembersParamsType): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'patch',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/disconnect-members/${centerId}`,
                headers: { authorization: accessToken },
                data: {
                    memberIds,
                },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            // 공유 풀 소유자는 409 SHARE_OWNER_DISCONNECT_BLOCKED로 연결해제가 막힌다.
            // 분기 없는 상태코드를 undefined로 삼키면 호출부가 res.status에서 터져 무음 실패한다.
            const status = error?.response?.status ?? 0;
            const data = error?.response?.data;
            if (status === 500) alert('서버 내부 오류');
            return { status, data };
        }
    };
export /**
 * @method patch
 * @brief 관리자의 회원 연결해제 API

 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1136/src/docs/Center.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%ED%9A%8C%EC%9B%90-%EC%97%B0%EA%B2%B0%ED%95%B4%EC%A0%9C-api
 */
    const patchDisconnectMember = async ({
        centerId,
        coachId,
        memberId,
        accessToken,
    }: PatchDisconnectMemberParamsType): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'patch',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/disconnect-member/${centerId}`,
                headers: { authorization: accessToken },
                data: {
                    coachId,
                    memberId,
                },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            // 공유 풀 소유자는 409 SHARE_OWNER_DISCONNECT_BLOCKED로 삭제·연결해제가 막힌다.
            // 상태와 서버 본문(errorCode)을 넘겨야 호출부가 '공유를 먼저 종료하라'고 안내할 수 있다.
            return { status: error?.response?.status ?? 0, data: error?.response?.data };
        }
    };
export /**
 * @method patch
 * @brief 관리자의 회원 연결해제 API
 * @link https://github.com/mustMoveProject/rappo-project/blob/develop/src/docs/Center.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%ED%9A%8C%EC%9B%90-%EB%8B%B4%EB%8B%B9%EA%B0%95%EC%82%AC-%EB%B3%80%EA%B2%BD-api
 */
    const patchChangeCoachForMember = async ({
        centerId,
        coachId,
        memberId,
        newCoachId,
        accessToken,
    }: PatchDisconnectMemberParamsType & { newCoachId: string }): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'patch',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/change-assigned-coach-v2/${centerId}`,
                headers: { authorization: accessToken },
                data: {
                    coachId,
                    memberId,
                    newCoachId,
                },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            // 400·409(공유 소유자·환불 진행 중) 등 어떤 실패든 상태와 서버 본문(errorCode·message)을
            // 넘긴다. 예전에는 나열한 상태만 돌려줘 나머지가 undefined 로 삼켜지고 안내가 없었다.
            // 네트워크 오류는 응답이 없으므로 status 0.
            return { status: error?.response?.status ?? 0, data: error?.response?.data };
        }
    };

export /**
 * @method patch
 * @brief 관리자의 회원 담당 강사 일괄 변경 API
 * @link https://github.com/mustMoveProject/rappo-project/blob/develop/src/docs/Center.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%ED%9A%8C%EC%9B%90-%EB%8B%B4%EB%8B%B9%EA%B0%95%EC%82%AC-%EB%B3%80%EA%B2%BD-api
 */
    const patchChangeCoachForMembers = async ({
        centerId,
        memberIds,
        newCoachId,
        accessToken,
    }: PatchChangeCoachForMembersParamsType): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'patch',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/bulk-change-assigned-coach-v2/${centerId}`,
                headers: { authorization: accessToken },
                data: {
                    memberIds,
                    newCoachId,
                },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            // 400·409(공유 소유자·환불 진행 중) 등 어떤 실패든 상태와 서버 본문(errorCode·message)을
            // 넘긴다. 예전에는 나열한 상태만 돌려줘 나머지가 undefined 로 삼켜지고 안내가 없었다.
            // 네트워크 오류는 응답이 없으므로 status 0.
            return { status: error?.response?.status ?? 0, data: error?.response?.data };
        }
    };

export /**
 * @method get
 * @brief 관리자의 매출정산 - 매출 달력 조회
 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1194/src/docs/Center.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%EB%A7%A4%EC%B6%9C%EC%A0%95%EC%82%B0---%EB%A7%A4%EC%B6%9C-%EB%8B%AC%EB%A0%A5-%EC%A1%B0%ED%9A%8C
 */
    const getCenterSalesSettlementCalendar = async (
        centerId: string,
        targetMonth: string,
        targetCoach: string | 'all',
        accessToken: string
    ): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'get',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/sales-calendar/${centerId}?targetMonth=${targetMonth}&targetCoach=${targetCoach}`,
                headers: { authorization: accessToken },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // 재등록 수업료용 양도 조회 실패(503) — 0원 달력 대신 오류 상태로 보이게 던진다(캐시되지 않는다).
            if (isSettlementTransferLookupFailure(error?.response?.status, error?.response?.data)) {
                throw new SettlementTransferLookupError();
            }
            // Error 😨
            switch (error.response.status) {
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };
                case 406:
                    return { status: error.response.status };
                case 500:
                    alert('서버 내부 오류');
                    return { status: error.response.status };
            }
        }
    };

export /**
 * @method get
 * @brief 관리자의 매출정산 미수업금액 조회
 * @link https://github.com/mustMoveProject/rappo-project/blob/develop/src/docs/Center.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%EB%A7%A4%EC%B6%9C%EC%A0%95%EC%82%B0-%EB%AF%B8%EC%88%98%EC%97%85%EA%B8%88%EC%95%A1-%EC%A1%B0%ED%9A%8C
 */
    const getCenterRemainingScheduleFee = async (
        centerId: string,
        targetMonth: string,
        targetCoach: string,
        accessToken: string
    ): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'get',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/remain-schedule-fee?targetMonth=${targetMonth}&targetCoach=${targetCoach}`,
                headers: { authorization: accessToken },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            switch (error.response.status) {
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };
                case 406:
                    return { status: error.response.status };
                case 500:
                    alert('서버 내부 오류');
                    return { status: error.response.status };
            }
        }
    };
export /**
 * @method get
 * @brief 관리자의 매출정산 남은 수업수 조회
 * @link https://github.com/mustMoveProject/rappo-project/blob/develop/src/docs/Center.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%EB%A7%A4%EC%B6%9C%EC%A0%95%EC%82%B0-%EB%82%A8%EC%9D%80-%EC%88%98%EC%97%85%EC%88%98-%EC%A1%B0%ED%9A%8C
 */
    const getCenterRemainingScheduleSession = async (
        centerId: string,
        targetMonth: string,
        targetCoach: string,
        accessToken: string
    ): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'get',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/remain-schedule-session?targetMonth=${targetMonth}&targetCoach=${targetCoach}`,
                headers: { authorization: accessToken },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            switch (error.response.status) {
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };
                case 406:
                    return { status: error.response.status };
                case 500:
                    alert('서버 내부 오류');
                    return { status: error.response.status };
            }
        }
    };

export /**
 * @method get
 * @brief 강사별 세션리뷰 권한 조회
 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1218/src/docs/Center.md#%EA%B0%95%EC%82%AC%EB%B3%84-%EC%84%B8%EC%85%98%EB%A6%AC%EB%B7%B0-%EA%B6%8C%ED%95%9C-%EC%A1%B0%ED%9A%8C
 */
    const getSessionReviewPermission = async (centerId: string, accessToken: string): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'get',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/review-permissions`,
                headers: { authorization: accessToken },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            switch (error.response.status) {
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };
                case 406:
                    return { status: error.response.status };
                case 500:
                    alert('서버 내부 오류');
                    return { status: error.response.status };
            }
        }
    };

export /**
 * @method patch
 * @brief 강사별 세션리뷰 권한 일괄 수정
 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1218/src/docs/Center.md#%EA%B0%95%EC%82%AC%EB%B3%84-%EC%84%B8%EC%85%98%EB%A6%AC%EB%B7%B0-%EA%B6%8C%ED%95%9C-%EC%9D%BC%EA%B4%84-%EC%88%98%EC%A0%95
 */
    const patchSessionReviewPermission = async ({
        centerId,
        permissions,
        accessToken,
    }: PatchSessionReviewPermissionRequestType & { accessToken: string }): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'patch',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/review-permissions`,
                headers: { authorization: accessToken },
                data: { reviewPermissions: permissions },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            switch (error.response.status) {
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };
                case 406:
                    return { status: error.response.status };
                case 500:
                    alert('서버 내부 오류');
                    return { status: error.response.status };
            }
        }
    };

export /**
 * @method get
 * @brief 강사별 권한 조회
 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1225/src/docs/Center.md#%EA%B0%95%EC%82%AC%EB%B3%84-%EA%B6%8C%ED%95%9C-%EC%A1%B0%ED%9A%8C
 */
    const getCoachPermission = async (centerId: string, accessToken: string): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'get',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/coach-permissions`,
                headers: { authorization: accessToken },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            switch (error.response.status) {
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };
                case 406:
                    return { status: error.response.status };
                case 500:
                    alert('서버 내부 오류');
                    return { status: error.response.status };
            }
        }
    };
export /**
 * @method patch
 * @brief 강사별 권한 일괄 수정
 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1225/src/docs/Center.md#%EA%B0%95%EC%82%AC%EB%B3%84-%EA%B6%8C%ED%95%9C-%EC%9D%BC%EA%B4%84-%EC%88%98%EC%A0%95
 */
    const patchCoachPermission = async ({
        centerId,
        permissions,
        accessToken,
    }: PatchCoachPermissionRequestType & { accessToken: string }): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'patch',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/coach-permissions`,
                headers: { authorization: accessToken },
                data: { coachPermissions: permissions },
            });

            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            switch (error.response.status) {
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };
                case 406:
                    return { status: error.response.status };
                case 500:
                    alert('서버 내부 오류');
                    return { status: error.response.status };
            }
        }
    };

export /**
 * @method patch
 * @brief 강사 순서 변경
 * @link https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1231/src/docs/Center.md#%EA%B0%95%EC%82%AC-%EC%88%9C%EC%84%9C-%EB%B3%80%EA%B2%BD
 */
    const patchCoachOrder = async (centerId: string, coachIds: string[], accessToken: string): Promise<any> => {
        let response = {} as AxiosResponse<any, any>;
        try {
            response = await axios({
                method: 'patch',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/coach-order/${centerId}`,
                headers: { authorization: accessToken },
                data: { coachIds },
            });
            if (response.status === 200) {
                return response;
            }
        } catch (error: any) {
            // Error 😨
            switch (error.response.status) {
                case 400:
                    return { status: error.response.status };
                case 401:
                    return { status: error.response.status };
                case 403:
                    return { status: error.response.status };
                case 404:
                    return { status: error.response.status };
                case 500:
                    alert('서버 내부 오류');
            }
        }
    };

export type PatchSessionReviewPermissionRequestType = {
    centerId: string;
    //      "permissions": [
    //     {
    //       "coachId": "63f1a1b2c3d4e5f678901234",
    //       "isReviewPermission": true
    //     },
    //     {
    //       "coachId": "63f1a1b2c3d4e5f678901235",
    //       "isReviewPermission": false
    //     }
    //   ]
    permissions: {
        coachId: string;
        isReviewPermission: boolean;
    }[];
};
export type PatchCoachPermissionRequestType = {
    centerId: string;
    permissions: {
        coachId: string;
        isMembershipPermissionAll: boolean;
        isMembershipPermissionUpdate: boolean;
        isPastSchedulePermissionUpdate: boolean;
    }[];
};

export type GetCenterMemberListRemainingPeriod =
    | 'all' // 전체 회원
    | 'lessThanOneWeek' //  7일 미만
    | 'lessThanTwoWeeks' //  14일 미만
    | 'lessThanOneMonth' //  30일 미만
    | 'lessThanHundredDays' //  100일 미만
    | 'lessThanOneYear' //  1년 미만
    | 'oneYearOrMore' //  1년 이상
    | 'expired'; //  기간 만료 (활성인데 유효기간이 지난 수강권 보유) — 강사 목록 클라 필터 전용

export type GetCenterMemberListRemainingSessions =
    | 'all' //  전체회원
    | 'lessThanThreeTimes' //  3회 미만
    | 'lessThanFiveTimes' //  5회 미만
    | 'lessThanTenTimes' //  10회 미만
    | 'lessThanFifteenTimes' //  15회 미만
    | 'lessThanTwentyTimes' //  20회 미만
    | 'twentyTimesOrMore'; //  20회 이상

export type GetCenterMemberListStatus =
    | 'all' //  전체 회원
    | 'active' //  수강 중
    | 'expired' //  만료
    | 'none'; //  대기 (회원권 없음)

export type GetCenterMemberListResponse = CenterMemberList;

export interface Membership {
    _id: string;
    period: number;
    BGColor: string[];
    status: string;
    isDeleted: boolean;
    isCanceled: boolean;
    /**
     * 그룹수업 통합(ReservationRules.includeGroupClassInSettlement) ON 시 MemberTicket을
     * Membership shape으로 감싼 row 구분자. 'group'일 때 _id는 실제 MemberTicket._id이며
     * Membership 전용 API(예: /v1/memberships/:id/custom-commission) 호출 대상이 아니다.
     */
    source?: 'pt' | 'group';
    /**
     * 만료 정책 — 2026-04 도메인 통일 작업에서 추가.
     *  - 'COUNT'  : 회차 소진 시 만료 (PT default — 기존 동작 유지)
     *  - 'PERIOD' : 기간 만료 시 만료
     *  - 'FIRST'  : 둘 중 먼저 도래 시 만료
     * 백엔드 `BACKEND_RESPONSE_MEMBERSHIP_UNIFIED_V1.md` §1 REQ-1/REQ-2 참조.
     */
    expiryPolicy?: 'COUNT' | 'PERIOD' | 'FIRST';
    coachId: string;
    memberId: string;
    name: string;
    activationDate: string;
    expirationDate: string;
    startSessionValue: number;
    totalSession: number;
    memo: string;
    issuer: Issuer;
    pricePerSchedule: number;
    totalPrice: number;
    membershipTemplateId: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
    id: string;
    currentSession: number;
    remainSession: number;
    remainPeriod: number;
    customedMonthlyCommissions?: {
        year: string;
        month: string;
        commission: number;
        commissionType: 'fixed' | 'percentage';
    }[];
}

export interface CenterMember {
    name: string;
    memberId: string;
    status: string;
    coachName: string;
    coachId: string;
    phoneNumber: string;
    age: number;
    gender: string;
    created: string;
    lastScheduleCreated: string;
    memberships: Membership[];
    isConnected: boolean;
    memberType: 'original' | 'temp' | 'public';
}
interface CenterMemberList {
    centerMemberList: CenterMember[];
}

export type Issuer = {
    issuerId?: string;
    name?: string;
    issuerRole?: 'admin' | 'coach';
};

export type GetCoachName = {
    coachId: string;
    name: string;
    profileImageUrl?: string;
    isDeleted?: boolean;
};

export type GetCoachNameListResponse = {
    success: boolean;
    coachNameList: GetCoachName[];
};

export type PatchCenterUpdateMemberParamsType = {
    centerId?: string;
    memberId?: string;
    name?: string;
    gender?: string;
    birthday?: string;
    phoneNumber?: string;
    memo?: string;
    newCoachId?: string;
    address?: string;
    goal?: { type: string; context?: string };
    accessToken?: string;
};
export type PatchDisconnectMembersParamsType = {
    centerId?: string;
    memberIds?: string[];
    accessToken?: string;
};
export type PatchDisconnectMemberParamsType = {
    centerId?: string;

    coachId?: string;
    memberId?: string;

    accessToken?: string;
};

export type PatchChangeCoachForMembersParamsType = {
    centerId?: string;
    memberIds?: string[];
    newCoachId?: string;
    accessToken?: string;
};
// [
//   {
//     "coachId": "63f1a1b2c3d4e5f678901234",
//     "isMembershipPermissionAll": true,
//     "isMembershipPermissionUpdate": false,
//     "isPastSchedulePermissionUpdate": true
//   },
//   {
//     "coachId": "63f1a1b2c3d4e5f678901235",
//     "isMembershipPermissionAll": false,
//     "isMembershipPermissionUpdate": true,
//     "isPastSchedulePermissionUpdate": false
//   }
// ]
export type GetCoachPermissionResponse = {
    coachId: string;
    coachName: string;
    isMembershipPermissionAll: boolean;
    isMembershipPermissionUpdate: boolean;
    isPastSchedulePermissionUpdate: boolean;
}[];

export type PostAssignCoachParamsType = {
    centerId: string;
    coachId: string;
    memberId: string;
    accessToken: string;
};

export const postAssignCoach = async ({
    centerId,
    coachId,
    memberId,
    accessToken,
}: PostAssignCoachParamsType): Promise<any> => {
    try {
        const response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/assign-coach/${centerId}`,
            headers: { authorization: accessToken },
            data: { coachId, memberId },
        });
        return response;
    } catch (error: any) {
        return { status: error?.response?.status ?? 500 };
    }
};

export const getCenterFeatureFlagsAPI = async (centerId: string, accessToken: string): Promise<any> => {
    const response = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/feature-flags`,
        headers: { authorization: accessToken },
    });
    return response;
};

// ─────────────────────────────────────────────────────────────
// 회원 알림 설정 (센터별 수강권 만료 안내 발송 설정)
// 계약: rappo `docs/CENTER_MEMBER_EXPIRY_ALERT_SETTING.md` 4-2
// ─────────────────────────────────────────────────────────────

/**
 * 실패 응답을 `{ status, data }` 로 바꾼다.
 *
 * 🔴 이 파일의 다른 함수들처럼 일부 상태만 골라 돌려주거나 500 에 `alert()` 를 띄우지 않는다.
 *    나열되지 않은 상태가 `undefined` 로 새어 호출부가 터진 적이 있다
 *    (`src/api/memberShip.updateFailure.test.ts`). 응답이 없으면 `status: 0`.
 *    열람 모드 쓰기는 전역 인터셉터가 전송 전에 403 `VIEW_ONLY` 로 거절하므로 같은 모양으로 온다.
 */
const toMemberNotificationSettingsFailure = (error: any): MemberNotificationSettingsApiResult => {
    const response = error?.response;
    if (response && typeof response.status === 'number') {
        return { status: response.status, data: response.data ?? null };
    }
    return { status: 0, data: null };
};

export /**
 * @method get
 * @brief 회원 알림 설정 조회 — 서버가 아는 키를 전부 불리언으로 채워 보낸다
 * @returns 성공·실패 모두 `{ status, data }`. 던지지 않는다.
 */
const getMemberNotificationSettingsAPI = async (
    centerId: string,
    accessToken: string,
): Promise<MemberNotificationSettingsApiResult> => {
    try {
        const response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/${encodeURIComponent(centerId)}/member-notification-settings`,
            headers: { authorization: accessToken },
        });
        return { status: response.status, data: response.data ?? null };
    } catch (error: any) {
        return toMemberNotificationSettingsFailure(error);
    }
};

export /**
 * @method patch
 * @brief 회원 알림 설정 변경 — 본문에는 **바꾼 키만** 넣는다 (`buildMemberNotificationSettingsPatch`)
 * @returns 성공·실패 모두 `{ status, data }`. 던지지 않는다. 성공 판정은 `status === 200` 만.
 *          200 본문은 저장 후 서버가 다시 읽은 전체 상태다(GET 과 같은 모양).
 */
const patchMemberNotificationSettingsAPI = async ({
    centerId,
    body,
    accessToken,
}: {
    centerId: string;
    body: MemberNotificationSettingsPatchBody;
    accessToken: string;
}): Promise<MemberNotificationSettingsApiResult> => {
    try {
        const response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/${encodeURIComponent(centerId)}/member-notification-settings`,
            headers: { authorization: accessToken },
            data: body,
        });
        return { status: response.status, data: response.data ?? null };
    } catch (error: any) {
        return toMemberNotificationSettingsFailure(error);
    }
};
